// Replay limits: how many trucks one replay shows, and over how long (ST-00277, ST-00311)
//
// ONE source, two byte-identical copies (the client's Docker build context is client/ and the
// server's image copies only server/, so neither can import the other or a workspace package):
//   server/modules/lexistar/replay/limits.ts                     the source (GET /replay/batch)
//   client/src/components/pages/LexiStar/Replay/replayLimits.ts  a copy (the Trucks picker, the form)
// client/src/components/pages/LexiStar/Replay/replayLimits.copy.test.ts fails when the two differ:
// edit the server file, then `cp` it over the client one. Pure TypeScript with no imports.
//
// WHY THESE NUMBERS. ST-00277 measured 25 trucks x 10 h of 1 Hz fixes (900 000) at p50 3.0 s with
// RLS on, and gave the batch fixed totals: 1 000 000 fixes read, 60 000 track points and 50 000
// events returned, shared evenly by the trucks (replay/service.ts BATCH_*). The totals do not grow
// with the number of trucks, so 100 trucks cost the database, the JSON and the map what 25 did;
// what grows is a handful of small queries per truck and one map marker per truck. One rule keeps
// each truck's share as dense as it was at 25 trucks over a week (on average a fix every 15 s, a
// track point every 4 min, an event every 5 min): trucks x hours <= 25 x 7 days. Up to 25 trucks
// a replay covers 7 days; 50 trucks 3 days 12 hours; 100 trucks 1 day 18 hours.

/** The most trucks one replay shows (distinct asset keys in one GET /replay/batch). */
export const MAX_REPLAY_TRUCKS = 100;

/** The longest window of a replay of several trucks, in hours (one truck alone: 31 days). */
export const MAX_MULTI_RANGE_HOURS = 7 * 24;

/** Trucks x hours one replay of several trucks may cover: 25 trucks over 7 days, as measured. */
export const MAX_MULTI_TRUCK_HOURS = 25 * MAX_MULTI_RANGE_HOURS;

/** Up to this many trucks a replay covers the whole MAX_MULTI_RANGE_HOURS (25). */
export const FULL_RANGE_TRUCKS = Math.floor(MAX_MULTI_TRUCK_HOURS / MAX_MULTI_RANGE_HOURS);

/**
 * The asset keys the display rules' versions endpoint takes in one call: a replay sends its
 * trucks' keys (up to 4 each, fewer with many trucks), two each at MAX_REPLAY_TRUCKS. Bounded so
 * the GET stays well under the proxy's 8 KB request line.
 */
export const MAX_REPLAY_DISPLAY_KEYS = 2 * MAX_REPLAY_TRUCKS;

const HOUR_MS = 3600000;

/** The longest window, in whole hours, a replay of `trucks` trucks covers. */
export function maxMultiRangeHours(trucks: number): number {
  const n = Math.max(1, Math.floor(Number(trucks) || 1));
  return Math.min(MAX_MULTI_RANGE_HOURS, Math.floor(MAX_MULTI_TRUCK_HOURS / n));
}

/** The same in milliseconds. */
export function maxMultiRangeMs(trucks: number): number {
  return maxMultiRangeHours(trucks) * HOUR_MS;
}

/** The most trucks one replay of `spanMs` can show (0 when it is longer than 7 days). */
export function maxTrucksFor(spanMs: number): number {
  const hours = Math.max(1, Math.ceil(Number(spanMs) / HOUR_MS) || 1);
  if (hours > MAX_MULTI_RANGE_HOURS) return 0;
  return Math.min(MAX_REPLAY_TRUCKS, Math.floor(MAX_MULTI_TRUCK_HOURS / hours));
}

/** Whole hours as people say them: "7 days", "3 days 12 hours", "1 day 18 hours", "20 hours". */
export function rangeWords(hours: number): string {
  const h = Math.max(0, Math.floor(Number(hours) || 0));
  const d = Math.floor(h / 24);
  const r = h % 24;
  const days = d === 1 ? '1 day' : `${d} days`;
  const rest = r === 1 ? '1 hour' : `${r} hours`;
  if (!d) return rest;
  return r ? `${days} ${rest}` : days;
}
